import axios from "axios";

// Resolve the API base URL with a smart fallback chain:
//  1. If REACT_APP_BACKEND_URL is set AND matches the current page origin → use it
//     (dev preview mode where frontend + backend share a URL).
//  2. If REACT_APP_BACKEND_URL is set but points at a DIFFERENT origin than the
//     page → we're either running the dev server (port 3000) hitting the
//     preview backend, OR the prod build has a stale/wrong URL baked in.
//     In BOTH cases we prefer same-origin `/api` when the page has been served
//     from a real deployment (Caddy proxies both frontend and /api), because
//     a stale build-arg is the #1 cause of a silent /setup-2fa hang.
//  3. If empty → same origin (Docker/Caddy setup where /api and / share a host).
//
// The rule: **any time the current page origin can serve /api itself, prefer
// it over the baked-in URL.** We detect this by attempting a same-origin probe
// during module init and caching the winner.
const RAW_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

function resolveApiBase() {
  if (typeof window === "undefined") return `${RAW_BACKEND_URL}/api`;
  const pageOrigin = window.location.origin;
  if (!RAW_BACKEND_URL) return `${pageOrigin}/api`;
  try {
    const baked = new URL(RAW_BACKEND_URL).origin;
    if (baked === pageOrigin) return `${baked}/api`;
    // Cross-origin baked URL — safe for the dev preview (page is on
    // localhost:3000, backend is on the preview URL). But in a real
    // deployment the page IS the deployment domain, and any cross-origin call
    // would be silently blocked by CORS-with-credentials for an unregistered
    // origin. Heuristic: if the page origin is *not* localhost, prefer
    // same-origin — Caddy handles /api internally.
    const isLocalDev = /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?$/i.test(pageOrigin);
    return isLocalDev ? `${baked}/api` : `${pageOrigin}/api`;
  } catch (_) {
    return `${pageOrigin}/api`;
  }
}

export const API = resolveApiBase();

if (typeof window !== "undefined") {
   
  console.info(`[PayTrack] API base = ${API} · page origin = ${window.location.origin} · baked = ${RAW_BACKEND_URL || "(none)"}`);
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000, // 30s — prevents "Generating…" hangs forever on CORS/DNS failures
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (localStorage.getItem("demo_mode") === "1") config.headers["X-Demo-Mode"] = "1";
  const imp = localStorage.getItem("impersonate_id");
  if (imp && localStorage.getItem("demo_mode") === "1") config.headers["X-Impersonate"] = imp;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
       
      console.error(`[PayTrack] Request timed out: ${err.config?.method?.toUpperCase()} ${err.config?.url}. Check REACT_APP_BACKEND_URL.`);
    }
    // Self-heal: if the backend says the current bearer is enrollment-only
    // but the caller is trying to hit a normal API, the user got stuck with
    // a stale enroll token in localStorage after finishing 2FA. Clear it so
    // the next request re-authenticates cleanly. Skip the /auth/totp/*
    // endpoints themselves so the actual enrollment flow doesn't self-nuke.
    const url = err.config?.url || "";
    const detail = err.response?.data?.detail;
    const isEnrollOnlyBlock =
      err.response?.status === 403 &&
      typeof detail === "string" &&
      detail.toLowerCase().includes("enrollment-only");
    const isAuthFlow = url.includes("/auth/totp") || url.includes("/auth/me") || url.includes("/auth/diag");
    if (isEnrollOnlyBlock && !isAuthFlow) {
      console.warn("[PayTrack] Stale enroll token detected — clearing and redirecting to /login.");
      localStorage.removeItem("access_token");
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
